import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Wifi, Users, Swords } from "lucide-react";
import ConnectionMotif from "@/components/ConnectionMotif";
import FiberUnderline from "@/components/FiberUnderline";

const CARDS = [
  {
    icon: Wifi,
    title: "CONNESSIONE",
    text: "Condividiamo idee, visione e nuovi obiettivi.",
    glow: "rgba(245, 197, 66,0.35)",
    accent: "#f5c542",
  },
  {
    icon: Users,
    title: "SQUADRA",
    text: "Collaborazione, energia e gioco di squadra dentro e fuori dal campo.",
    glow: "rgba(230, 0, 0,0.35)",
    accent: "#e60000",
  },
  {
    icon: Swords,
    title: "SFIDA",
    text: "Mettiti in gioco nei tornei e vivi la competizione insieme ai tuoi colleghi.",
    glow: "rgba(245, 197, 66,0.35)",
    accent: "#f5c542",
  },
];

function TiltCard({ card, index }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 200, damping: 20 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ delay: index * 0.12, duration: 0.6 }}
      className="group relative rounded-2xl border border-white/10 bg-[#0f0f13] p-8"
      data-testid={`event-card-${index}`}
    >
      <div
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ boxShadow: `0 0 40px ${card.glow}`, border: `1px solid ${card.accent}55` }}
      />
      <div
        className="mb-6 grid h-14 w-14 place-items-center rounded-xl"
        style={{ background: `${card.accent}18`, color: card.accent, boxShadow: `0 0 20px ${card.glow}` }}
      >
        <card.icon size={26} />
      </div>
      <h3 className="font-display text-xl font-extrabold tracking-tight">{card.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-white/60">{card.text}</p>
    </motion.div>
  );
}

export default function EventSection() {
  return (
    <section className="relative isolate mx-auto w-full max-w-7xl overflow-hidden px-6 py-24 md:px-10 md:py-32" data-testid="event-section">
      <ConnectionMotif color="#f5c542" color2="#e60000" className="-z-10 opacity-[0.14]" />
      <div className="speed-line mb-16" />
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-xs font-semibold uppercase tracking-[0.3em] text-[#f5c542]"
      >
        L'Evento
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-4 max-w-3xl font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl lg:text-5xl"
      >
        NON SARÀ UNA CONVENTION COME LE ALTRE
      </motion.h2>
      <FiberUnderline />
      <p className="mt-6 max-w-2xl text-base text-white/60 md:text-lg">
        Una giornata pensata per incontrarsi, condividere obiettivi, creare nuove connessioni e vivere
        insieme un'esperienza fatta di sport, divertimento e spirito di squadra.
      </p>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {CARDS.map((c, i) => (
          <TiltCard key={c.title} card={c} index={i} />
        ))}
      </div>
    </section>
  );
}
